import { lockScroll, unlockScroll } from '@theme/utilities';

(() => {
  'use strict';

  function emit(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail, bubbles: true }));
  }

  class SouloChatWidget extends HTMLElement {
    #scrollLocked = false;

    connectedCallback() {
      this.launcher = this.querySelector('[data-soulo-chat-launcher]');
      this.panel = this.querySelector('[data-soulo-chat-panel]');
      this.closeBtn = this.querySelector('[data-soulo-chat-close]');
      this.conversation = this.querySelector('[data-soulo-chat-conversation]');
      this.form = this.querySelector('[data-soulo-chat-form]');
      this.input = this.querySelector('[data-soulo-chat-input]');
      this.quickActions = this.querySelectorAll('[data-soulo-chat-action]');
      this.faqTemplates = [...this.querySelectorAll('[data-soulo-chat-faq] template')];

      const stringsEl = document.getElementById('soulo-chat-strings');
      this.strings = stringsEl ? JSON.parse(stringsEl.textContent) : {};

      this.sectionId = this.dataset.searchSectionId || 'soulo-chat-search';

      this.launcher?.addEventListener('click', () => this.toggle());
      this.closeBtn?.addEventListener('click', () => this.close());

      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.hasAttribute('open')) {
          this.close();
          return;
        }
        if (event.key === 'Tab' && this.hasAttribute('open') && this.#isMobileModal()) {
          this.#trapTab(event);
        }
      });

      document.addEventListener('click', (event) => {
        if (this.hasAttribute('open') && !this.contains(event.target)) {
          this.close();
        }
      });

      this.quickActions.forEach((btn) => {
        btn.addEventListener('click', () => this.#runQuickAction(btn));
      });

      this.form?.addEventListener('submit', (event) => {
        event.preventDefault();
        const query = this.input.value.trim();
        if (!query) return;
        this.#handleUserMessage(query);
        this.input.value = '';
      });
    }

    toggle() {
      this.hasAttribute('open') ? this.close() : this.open();
    }

    open() {
      this.setAttribute('open', '');
      this.panel.hidden = false;
      this.launcher.setAttribute('aria-expanded', 'true');
      emit('soulo_chat_open', {});

      if (this.#isMobileModal()) {
        // Bottom-sheet mode reads as modal — use the theme's own
        // reference-counted scroll lock (same mechanism drawers/dialogs
        // use) rather than hand-rolling one. Locking body.style.overflow
        // directly would do nothing here anyway: this theme scrolls via
        // .page-wrapper / html[scroll-lock], not a plain body scrollbar.
        lockScroll(this);
        this.#scrollLocked = true;
      }

      // Synchronous, not rAF-deferred: `hidden` is already cleared above, so
      // the input is focusable immediately — a paint-frame callback isn't
      // needed and isn't guaranteed to fire promptly (background tab, low
      // power mode), which would leave focus stranded on the launcher.
      this.input?.focus();
    }

    close() {
      const wasOpen = this.hasAttribute('open');
      this.removeAttribute('open');
      this.panel.hidden = true;
      this.launcher.setAttribute('aria-expanded', 'false');

      if (this.#scrollLocked) {
        unlockScroll(this);
        this.#scrollLocked = false;
      }

      if (wasOpen) this.launcher?.focus();
    }

    #isMobileModal() {
      return window.matchMedia('(max-width: 749px)').matches;
    }

    #focusableElements() {
      return [...this.panel.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )].filter((el) => el.offsetParent !== null);
    }

    #trapTab(event) {
      const focusable = this.#focusableElements();
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = this.getRootNode().activeElement || document.activeElement;

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      } else if (!focusable.includes(active)) {
        // Focus escaped the panel (e.g. programmatic focus elsewhere) —
        // pull it back in rather than letting Tab continue into the page.
        event.preventDefault();
        first.focus();
      }
    }

    #addMessage(text, role = 'assistant') {
      const el = document.createElement('div');
      el.className = `soulo-chat__message soulo-chat__message--${role}`;
      el.textContent = text;
      this.conversation.appendChild(el);
      this.conversation.scrollTop = this.conversation.scrollHeight;
      return el;
    }

    #addRichAssistantBlock(introText, node) {
      if (introText) this.#addMessage(introText, 'assistant');
      const wrap = document.createElement('div');
      wrap.className = 'soulo-chat__message soulo-chat__message--assistant';
      wrap.appendChild(node);
      this.conversation.appendChild(wrap);
      this.conversation.scrollTop = this.conversation.scrollHeight;
    }

    #runQuickAction(btn) {
      const action = btn.dataset.souloChatAction;
      const href = btn.dataset.href;
      if (action === 'contact') emit('soulo_chat_contact', {});
      if (href) window.location.href = href;
    }

    #findFaqMatch(query) {
      const q = query.toLowerCase();
      return this.faqTemplates.find((tpl) => {
        const keyword = (tpl.dataset.faqKeyword || '').toLowerCase();
        return keyword && q.includes(keyword);
      });
    }

    async #handleUserMessage(query) {
      this.#addMessage(query, 'user');

      const faqMatch = this.#findFaqMatch(query);
      if (faqMatch) {
        this.#addMessage(faqMatch.content.textContent.trim(), 'assistant');
        const link = faqMatch.dataset.faqLink;
        const label = faqMatch.dataset.faqLinkLabel || this.strings.faqFallbackLabel;
        if (link) {
          const a = document.createElement('a');
          a.href = link;
          a.className = 'soulo-chat__chip';
          a.textContent = label;
          this.#addRichAssistantBlock('', a);
        }
        return;
      }

      await this.#searchProducts(query);
    }

    async #searchProducts(query) {
      emit('soulo_chat_search', { query });
      const loading = this.#addLoadingMessage();

      try {
        const url = `${this.strings.sectionUrl}&q=${encodeURIComponent(query)}&type=product`;
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Search request failed: ${response.status}`);
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const results = doc.querySelector('[data-soulo-chat-results]');

        loading.remove();

        if (!results || results.querySelector('[data-soulo-chat-empty]') || !results.children.length) {
          this.#renderNoResults(query);
          return;
        }

        this.#addMessage(this.strings.searchIntro || '', 'assistant');

        const list = document.createElement('div');
        list.className = 'soulo-chat__results';
        [...results.querySelectorAll('[data-soulo-chat-product]')].forEach((productEl) => {
          const clone = productEl.cloneNode(true);
          clone.addEventListener('click', () => {
            emit('soulo_chat_product_click', { url: clone.getAttribute('href') });
          });
          list.appendChild(clone);
        });

        this.#addRichAssistantBlock('', list);
      } catch (error) {
        loading.remove();
        this.#renderNoResults(query);
      }
    }

    #addLoadingMessage() {
      const el = document.createElement('div');
      el.className = 'soulo-chat__message soulo-chat__message--assistant soulo-chat__loading';
      el.setAttribute('aria-hidden', 'true');
      const text = document.createElement('span');
      text.textContent = this.strings.searching || '';
      const dots = document.createElement('span');
      dots.className = 'soulo-chat__loading-dots';
      dots.innerHTML = '<i></i><i></i><i></i>';
      el.append(text, dots);
      this.conversation.appendChild(el);
      this.conversation.scrollTop = this.conversation.scrollHeight;
      return el;
    }

    #renderNoResults(query) {
      const wrap = document.createElement('div');
      wrap.className = 'soulo-chat__no-results';

      const icon = document.createElement('span');
      icon.className = 'soulo-chat__no-results-icon';
      icon.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><circle cx="10.5" cy="10.5" r="6.5" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="m19.5 19.5-4.3-4.3" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>';
      wrap.appendChild(icon);

      const text = document.createElement('p');
      text.textContent = this.strings.noResults || '';
      wrap.appendChild(text);

      const a = document.createElement('a');
      a.href = `${this.strings.searchUrl}?q=${encodeURIComponent(query)}`;
      a.className = 'soulo-chat__chip';
      a.textContent = this.strings.viewProduct || '';
      wrap.appendChild(a);

      this.#addRichAssistantBlock('', wrap);
    }
  }

  if (!customElements.get('soulo-chat-widget')) {
    customElements.define('soulo-chat-widget', SouloChatWidget);
  }
})();
